import React, { useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import {
  Alert, Badge, Button, Col, FormGroup, Input, Label, Row, Spinner, Table,
} from 'reactstrap';
import { PREVIEW_COLOR_BATCH } from '../../query/query';
import { APPLY_COLOR_BATCH } from '../../mutation/mutation';
import readMacSpreadsheet from './mac-spreadsheet';

const TARGETS = {
  BW: {
    label: 'Turn into black & white',
    name: 'black & white',
    eligible: 'Color → will turn black & white',
    already: 'Already black & white',
  },
  COLOR: {
    label: 'Turn into color',
    name: 'color',
    eligible: 'B&W → will turn color',
    already: 'Already color',
  },
};

const STATUS_COLORS = { ELIGIBLE: 'primary', ALREADY_TARGET: 'warning', NOT_LOVEPIX: 'secondary' };

const statusText = (status, target) => ({
  ELIGIBLE: TARGETS[target].eligible,
  ALREADY_TARGET: TARGETS[target].already,
  NOT_LOVEPIX: 'B&W, not a Lovepix: cannot turn color',
}[status]);

const CONFIDENCE_COLORS = { HIGH: 'success', MEDIUM: 'info', LOW: 'secondary' };

const percent = (confidence) => `${Math.round(confidence * 100)}%`;

// Same cleaning as the API: "C-8831cdb119c8 " → "8831CDB119C8".
const cleanEntry = (entry) => String(entry).trim().replace(/^c\s*-/i, '').replace(/[\s:-]/g, '')
  .toUpperCase();

// The entries with each fixed one replaced by the box the agent chose.
const withFixes = (entries, fixes) => entries.map((entry) => (
  fixes[entry] || fixes[cleanEntry(entry)] || entry
));

// The same typo can appear several times in a file: shown once, with its count.
const countOccurrences = (items) => items.reduce(
  (counts, item) => counts.set(item, (counts.get(item) || 0) + 1), new Map(),
);

const MacList = ({
  title, color, items, columnWidth = 180,
}) => (
  items && items.length > 0 && (
    <Alert color={color}>
      <strong>{`${title} (${items.length})`}</strong>
      <div
        style={{
          fontFamily: 'monospace', marginTop: 8, maxHeight: 160, overflowY: 'auto', columnWidth,
        }}
      >
        {[...countOccurrences(items)].map(([item, count]) => (
          <div key={item} style={{ whiteSpace: 'nowrap' }}>{count > 1 ? `${item} ×${count}` : item}</div>
        ))}
      </div>
    </Alert>
  )
);

const Summary = ({
  fileName, sheetNames, fixCount, preview: { target, summary },
}) => (
  <Table size="sm" style={{ maxWidth: 560 }}>
    <tbody>
      <tr>
        <td>File</td>
        <td>{`${fileName}${sheetNames.length ? ` (sheet: ${sheetNames.join(', ')})` : ''}`}</td>
      </tr>
      {[
        ['Rows read', summary.entryCount],
        ['MACs fixed with a suggestion', fixCount],
        ['Unique MAC addresses', summary.uniqueMacCount],
        ['Duplicates ignored', summary.duplicateCount],
        ['Not a MAC address (to correct)', summary.invalidCount],
        ['Not found in the database', summary.missingCount],
        ['Boxes found (a MAC can match several)', summary.boxCount],
        [`${TARGETS[target].already} (warning)`, summary.alreadyTargetCount],
        ...(target === 'COLOR' ? [['B&W but not a Lovepix', summary.notLovepixCount]] : []),
      ].map(([label, value]) => (
        <tr key={label}>
          <td>{label}</td>
          <td>{value}</td>
        </tr>
      ))}
      <tr>
        <td><strong>{`To turn ${TARGETS[target].name}`}</strong></td>
        <td><strong>{summary.eligibleCount}</strong></td>
      </tr>
    </tbody>
  </Table>
);

const candidateText = (candidate) => (
  `${percent(candidate.confidence)} · ${candidate.macAddress} (${candidate.hardware || '?'}, ${candidate.hasColor ? 'color' : 'B&W'}) · ${candidate.mistake}`
);

/**
 * Boxes one typing mistake away from the entries that could not be used, the
 * likeliest first with its confidence. Only a HIGH one is chosen in advance;
 * anything else waits for the agent.
 */
const Suggestions = ({
  suggestions, choices, onChoose, onUse, disabled,
}) => {
  if (!suggestions || !suggestions.length) return null;
  const chosenCount = suggestions.filter((s) => choices[s.entry]).length;
  return (
    <Alert color="light" style={{ border: '1px solid #ddd' }}>
      <strong>{`Possible fixes for the MACs above (${suggestions.length})`}</strong>
      <div style={{ margin: '4px 0 8px' }}>
        Boxes one typing mistake away, the likeliest first. The percentage weighs how
        likely the mistake is (O/0, B/8, 8/E… are common; other characters rarely) and
        whether the fix lands on a manufacturer prefix of the file. From 80% it is chosen
        for you; check the label when in doubt. Only chosen fixes are used.
      </div>
      <Table size="sm" style={{ background: 'white' }}>
        <thead>
          <tr>
            <th>In the file</th>
            <th>Box to use</th>
            <th>Best guess</th>
          </tr>
        </thead>
        <tbody>
          {suggestions.map((s) => (
            <tr key={s.entry}>
              <td style={{ fontFamily: 'monospace' }}>{s.entry}</td>
              <td style={{ fontFamily: 'monospace' }}>
                <Input
                  type="select"
                  bsSize="sm"
                  value={choices[s.entry] || ''}
                  onChange={({ target }) => onChoose(s.entry, target.value)}
                >
                  <option value="">
                    {`Don't fix (${s.candidateCount} box${s.candidateCount > 1 ? 'es' : ''} one mistake away)`}
                  </option>
                  {s.candidates.map((c) => (
                    <option key={c.macAddress} value={c.macAddress}>{candidateText(c)}</option>
                  ))}
                </Input>
              </td>
              <td>
                <Badge color={CONFIDENCE_COLORS[s.confidence]}>
                  {percent(s.candidates[0].confidence)}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button color="primary" onClick={onUse} disabled={disabled || chosenCount === 0}>
        {`Use ${chosenCount} fix${chosenCount === 1 ? '' : 'es'} and preview again`}
      </Button>
    </Alert>
  );
};

const BoxTable = ({ boxes, target }) => (
  <details>
    <summary>{`Boxes found (${boxes.length})`}</summary>
    <Table size="sm" striped>
      <thead>
        <tr>
          <th>MAC address</th>
          <th>Pairing</th>
          <th>Hardware</th>
          <th>Color</th>
          <th>Made B&W by script</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        {boxes.map((box) => (
          <tr key={box.boxId}>
            <td style={{ fontFamily: 'monospace' }}>{box.macAddress}</td>
            <td>{box.pairing}</td>
            <td>{box.hardware}</td>
            <td>{box.hasColor ? 'Yes' : 'No'}</td>
            <td>{box.convertedToBlackAndWhiteManually ? 'Yes' : ''}</td>
            <td>
              <Badge color={STATUS_COLORS[box.status]}>{statusText(box.status, target)}</Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </details>
);

// Suggested fixes that are safe to use without a second look.
const preselected = (suggestions = []) => suggestions.reduce((choices, s) => (
  s.confidence === 'HIGH' ? { ...choices, [s.entry]: s.candidates[0].macAddress } : choices
), {});

/**
 * loveboxBatchManager role only: upload a factory list of MAC addresses (xlsx/csv),
 * fix the MACs that match no box, see the state of every box, then turn them all
 * black & white (the usual case) or color.
 */
const BatchColor = () => {
  const client = useApolloClient();
  const [applyBatch] = useMutation(APPLY_COLOR_BATCH);
  const [target, setTarget] = useState('BW');
  const [file, setFile] = useState(null);
  const [entries, setEntries] = useState([]);
  const [fixes, setFixes] = useState({});
  const [choices, setChoices] = useState({});
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const runPreview = async (list, chosenTarget) => {
    const { data } = await client.query({
      query: PREVIEW_COLOR_BATCH,
      variables: { macAddresses: list, target: chosenTarget },
      fetchPolicy: 'network-only',
    });
    const result = data.savPreviewColorBatch;
    setPreview(result);
    setChoices(preselected(result.suggestions));
  };

  const withLoading = async (action, failure) => {
    setError('');
    setLoading(true);
    try {
      await action();
    } catch (err) {
      setError(`${failure}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async ({ target: input }) => {
    const chosen = input.files && input.files[0];
    // Cleared so that choosing the same file again (after fixing it) reloads it.
    // eslint-disable-next-line no-param-reassign
    input.value = '';
    if (!chosen) return;
    setFile(null);
    setEntries([]);
    setFixes({});
    setPreview(null);
    setApplied(null);
    await withLoading(async () => {
      const { entries: read, sheetNames } = await readMacSpreadsheet(chosen);
      setFile({ name: chosen.name, sheetNames });
      if (!read.length) {
        setError(`No MAC address column found in ${chosen.name}.`);
        return;
      }
      setEntries(read);
      await runPreview(read, target);
    }, `Could not read ${chosen.name}`);
  };

  const handleTarget = async (chosenTarget) => {
    setTarget(chosenTarget);
    setApplied(null);
    if (!entries.length) return;
    await withLoading(
      () => runPreview(withFixes(entries, fixes), chosenTarget),
      'Preview failed',
    );
  };

  const handleUseFixes = async () => {
    const nextFixes = Object.entries(choices).reduce(
      (all, [entry, mac]) => (mac ? { ...all, [entry]: mac } : all), fixes,
    );
    setFixes(nextFixes);
    await withLoading(
      () => runPreview(withFixes(entries, nextFixes), target),
      'Preview failed',
    );
  };

  const handleApply = async () => {
    const count = preview.summary.eligibleCount;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Turn ${count} Lovebox${count > 1 ? 'es' : ''} ${TARGETS[target].name}?`)) return;
    await withLoading(async () => {
      const { data } = await applyBatch({
        variables: { macAddresses: withFixes(entries, fixes), target },
      });
      const result = data.savApplyColorBatch;
      setApplied(result);
      setPreview(result.after);
    }, 'Conversion failed');
  };

  const alreadyTarget = preview
    ? preview.boxes.filter((box) => box.status === 'ALREADY_TARGET').map((box) => box.macAddress)
    : [];
  const fixList = Object.entries(fixes).map(([entry, mac]) => `${entry} → ${mac}`);

  return (
    <div>
      <FormGroup tag="fieldset">
        {Object.entries(TARGETS).map(([value, { label }]) => (
          <FormGroup check inline key={value}>
            <Label check>
              <Input
                type="radio"
                name="batch-color-target"
                checked={target === value}
                onChange={() => handleTarget(value)}
                disabled={loading}
              />
              {label}
            </Label>
          </FormGroup>
        ))}
      </FormGroup>
      <FormGroup>
        <Label>
          Upload the list of MAC addresses (xlsx or csv, e.g. the factory file with a
          &quot;Serial #&quot; column of C-… serials). Nothing changes until you confirm.
        </Label>
        <Row>
          <Col md={6}>
            <Input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFile}
              disabled={loading}
            />
          </Col>
          <Col>{loading && <Spinner size="sm" color="primary" />}</Col>
        </Row>
      </FormGroup>
      {error && <Alert color="danger">{error}</Alert>}
      {applied && (
        <div>
          <Alert color="success">
            {`${applied.modifiedCount} box${applied.modifiedCount === 1 ? '' : 'es'} turned ${TARGETS[applied.after.target].name}. The table below is the state after the change.`}
          </Alert>
          <MacList title="Converted — keep this list" color="success" items={applied.convertedMacs} />
        </div>
      )}
      {preview && file && (
        <div>
          <Summary
            fileName={file.name}
            sheetNames={file.sheetNames}
            fixCount={fixList.length}
            preview={preview}
          />
          <Button
            color="danger"
            onClick={handleApply}
            disabled={loading || preview.summary.eligibleCount === 0}
          >
            {preview.summary.eligibleCount === 0
              ? `Nothing to turn ${TARGETS[preview.target].name}`
              : `Turn ${preview.summary.eligibleCount} ${TARGETS[preview.target].name}`}
          </Button>
          <br />
          <br />
          <MacList
            title={`${TARGETS[preview.target].already} — unexpected in a factory list, check these boxes`}
            color="warning"
            items={alreadyTarget}
          />
          <MacList title="Not a MAC address — correct them in the file" color="warning" items={preview.invalidEntries} />
          <MacList title="Not found in the database" color="danger" items={preview.missingMacs} />
          <Suggestions
            suggestions={preview.suggestions}
            choices={choices}
            onChoose={(entry, mac) => setChoices({ ...choices, [entry]: mac })}
            onUse={handleUseFixes}
            disabled={loading}
          />
          <MacList title="Fixed with a suggestion (file → box)" color="info" items={fixList} columnWidth={360} />
          <MacList title="Several boxes have these MACs — each box is counted" color="info" items={preview.sharedMacs} />
          <BoxTable boxes={preview.boxes} target={preview.target} />
        </div>
      )}
    </div>
  );
};

export default BatchColor;



// WEBPACK FOOTER //
// ./src/components/lovebox-info/batch-color.js